import {
    DeliveryAddress,
    Discount,
    Order,
    OrderItem,
    OrderState,
    OrderStatus,
    OrderType,
    ProductType,
    Unit
} from '@us/web/shared/data-access';
import { uniqueId } from './helper.util';
import { parseISO } from 'date-fns';
import { sortBy } from 'lodash';

export const validateDeliveryAddress = (address: DeliveryAddress, orderType: OrderType | null) =>
    orderType !== OrderType.Delivery || !!(address.name && address.phone && address.address);

export const getFormattedAddress = (deliveryAddress: DeliveryAddress): string => {
    const { address, ward_name, district_name, city_name } = deliveryAddress;
    return [address, ward_name, district_name, city_name].filter(Boolean).join(', ');
};

export const calcTotalPrice = (items: OrderItem[]): number =>
    items.reduce(
        (total, item) =>
            total +
            (item.state &&
            [OrderState.Rejected, OrderState.Canceled, OrderState.Merged].includes(+item.state as OrderState)
                ? 0
                : (item.price - (item.discount?.price || 0) + (item.tax?.price || 0) + (item.surcharge?.price || 0)) *
                  item.quantity),
        0
    );

export const calcTotalPriceV2 = (
    items: OrderItem[],
    id = 0
): number => // tổng tiền chưa giảm giá
    items.reduce(
        (total, item) =>
            total +
            (item.state &&
            [OrderState.Rejected, OrderState.Canceled, OrderState.Merged].includes(+item.state as OrderState)
                ? 0
                : (item.unit.product_price ? item.unit.product_price : item.price) * item.quantity),
        0
    );

export const calcTotalAmountById = (
    items: OrderItem[],
    id = 0,
    is_category = false
): number => // tinnhs tông so lượng theo id
    items.reduce(
        (total, item) =>
            total +
            (item.state &&
            [OrderState.Rejected, OrderState.Canceled, OrderState.Merged].includes(+item.state as OrderState)
                ? 0
                : is_category && item.category_id == id
                ? Number(item.quantity)
                : !is_category && +item.product_id == id
                ? Number(item.quantity)
                : 0),
        0
    );

export const calcTotalItems = (items: OrderItem[]): number =>
    items.reduce(
        (total, item) =>
            total +
            (item.state &&
            [OrderState.Rejected, OrderState.Canceled, OrderState.Merged].includes(+item.state as OrderState)
                ? 0
                : Number(item.quantity)),
        0
    );

export const calcTotalItemsV2 = (items: OrderItem[]): number =>
    items.reduce(
        (total, item) =>
            total +
            ((item.state &&
                [OrderState.Rejected, OrderState.Canceled, OrderState.Merged].includes(+item.state as OrderState)) ||
            item.price === 0
                ? 0
                : Number(item.quantity)),
        0
    );

export const calcTotalDuration = (items: OrderItem[]) =>
    items.reduce(
        (total, item) =>
            total +
            (item.state &&
            [OrderState.Rejected, OrderState.Canceled, OrderState.Merged].includes(+item.state as OrderState)
                ? 0
                : Number(item.duration || 0)),
        0
    );

export const parseDataServer = ({ current_state, states, items, ...value }: any): Order => {
    const cartItems: OrderItem[] = [];
    for (const { unit_id, unit_name, unit_ratio, state, parent_uuid, ...itemValue } of sortBy(
        items,
        item => !!item.parent_uuid
    )) {
        if (+parent_uuid) {
            const index = cartItems.findIndex(item => item.uuid === parent_uuid);
            if (index === -1) {
                console.error('Parent item not found');
                continue;
            }
            cartItems[index].combo?.push({
                ...itemValue,
                unit: {
                    id: unit_id,
                    name: unit_name,
                    ratio: unit_ratio,
                    old_price: null
                } as Unit,
                uuid: itemValue.uuid || uniqueId(),
                state: +state as OrderState
            });
        } else {
            cartItems.push({
                ...itemValue,
                unit: {
                    id: unit_id,
                    name: unit_name,
                    ratio: unit_ratio,
                    old_price: null
                } as Unit,
                uuid: itemValue.uuid || uniqueId(),
                state: +state as OrderState,
                combo: []
            });
        }
    }

    return {
        ...value,
        items: cartItems,
        states: (states || [])
            .map((item: any) => ({
                state: +item.state as OrderState,
                message: item.message,
                created_at: item.created_at,
                updated_at: item.updated_at
            }))
            .sort(
                (a: OrderStatus, b: OrderStatus) =>
                    (parseISO(b.created_at as unknown as string) || new Date()).getTime() -
                    (parseISO(a.created_at as unknown as string) || new Date()).getTime()
            ),
        total_items: calcTotalItems(cartItems),
        total_price: calcTotalPrice(cartItems),
        current_state: +current_state as OrderState
    };
};

export const calcTotalDiscount = (discount: Discount | undefined, totalPrice: number, is_rank: boolean = false) => {
    const percent = discount?.percent || 0;
    const price = discount?.price || 0;
    const price_rank = discount?.price_rank || 0;
    if (percent > 0 && percent <= 100) {
        const totalDiscount = (totalPrice * percent) / 100;
        const maxPriceDiscount = +(discount?.maxPrice || 0);
        return maxPriceDiscount != 0 ? Math.min(totalDiscount, maxPriceDiscount) : totalDiscount;
    }
    return is_rank ? price_rank:price;
};

export const orderCartItemDefault = (
    promotion_type: number,
    promotion_subtype: number | null,
    promotion_subtype2: number | null,
    product_id: string,
    product_name: string,
    amount: number,
    unit_id: number,
    unit_name: string,
    gift_food: number,
    discount_id = 0,
    surcharge_id = 0,
    reason = '',
    options: any = [],
    gift_category = 0,
    gift_category_id = 0
) => {
    const orderItemDefault: OrderItem = {
        uuid: uniqueId(),
        product_type: ProductType.Goods,
        combo: [],
        extra_discount: {
            promotion_type: promotion_type,
            promotion_subtype: promotion_subtype,
            promotion_subtype2: promotion_subtype2,
            group_local_id: ''
        },
        note: '',
        options: options,
        price: 0,
        product_id: product_id,
        product_name: product_name,
        quantity: amount,
        unit: {
            id: unit_id,
            is_primary: false,
            name: unit_name,
            old_price: 0,
            price: 0,
            ratio: 1
        },
        discount: {
            id: discount_id,
            price: 0,
            percent: 0,
            reason: discount_id ? reason : ''
        },
        surcharge: {
            id: surcharge_id,
            price: 0,
            percent: 0,
            reason: surcharge_id ? reason : ''
        },
        gift_food: gift_food,
        gift_category: gift_category,
        gift_category_id: gift_category_id,
        category_id: gift_category_id,
        version_code: 0
    };
    return orderItemDefault;
};
